// §4.12 Stock Adjustment (office) — spec 06 §4.12, D-315.
//
// WHY IT EXISTS. §5 row 25's `adjustment` and row 48's explicit hold had no
// office screen: the unit card's Move posts a repack (which cannot change a
// quantity) and §4.11 only decides adjustments raised elsewhere. D-308 then
// told storekeepers, in three module-04 sentences, to record leftover material
// "with a stock adjustment in Inventory", and none of them could link anywhere.
//
// WHAT IT POSTS. A bulk quantity of one product (and its batch) between the
// stock location and a counter location of the same plant, in the ledger's own
// convention (lib/adjust.ts): an increase from `supplier` (found) or
// `production` (work in progress), a decrease to `scrap` or `production`. The
// §3.9 value gate decides whether it posts (201) or waits for a manager (202),
// and the storekeeper cannot know which in advance (no cost read, no settings
// read), so the screen says which happened and links to the ledger or to the
// request's own page. "Send for approval anyway" is row 48, POST
// /adjustment-requests, which always holds.
//
// THE FORM'S OWN RULES (stated in the spec because the server does not hold
// them): the reasons follow the direction, a tracked product needs a batch, a
// decrease cannot exceed the loose stock at that place, and an increase out of
// work in progress cannot exceed that batch's work in progress while it is at
// or above zero (below zero it is an order's output, spec 04 S7's R1). A truck
// is written off to scrap only and always through approval (§9.10). A decrease
// into work in progress keeps quality's D-211 fence. A container named by the
// link follows D-308 round 2, and a serial or measured-unit gain needs the
// person to say it is loose. The limits are checked only in the base unit.
//
// ONE KEY PER INTENT (useIntentKey, the D-308 rule): a press after a dropped
// response replays the stored answer instead of posting a second adjustment,
// and the form LOCKS while that outcome is unknown, so no edit can turn the
// retry into a second, different adjustment under a new key.
//
// NOT ON THE FLOOR. A money-gated write that can wait for a manager does not
// belong in the replay outbox, and floor_worker holds no `inventory.adjust`.
// The phone form is this office route at 412 px (NET-M06-RESPONSIVE).

import { shortId } from "../../../../lib/short-id";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { CheckboxField, Field } from "../../../../components/field";
import { SegmentedControl, Select } from "../../../../components/select";
import {
  CardSkeleton,
  EmptyState,
  ErrorState,
  ForbiddenState,
  OfflineState,
} from "../../../../components/states";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { ProductPicker } from "../../catalogue/components/product-picker";
import { outcomeUnknown, useIntentKey } from "../../production/hooks/intent-key";
import { LocationPicker } from "../components/location-picker";
import {
  useAdjustStock,
  useFacilities,
  useLevels,
  useLocationPathIndex,
  useLot,
  useLots,
  usePlantLocationOfKind,
  useProductCard,
  useProductConversions,
  useStockUnit,
  useStockUnits,
  useUomIndex,
  useWorkOrderRef,
  type AdjustOutcome,
  type StockLevel,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import {
  adjustmentBody,
  checkQuantity,
  containerRule,
  COUNTER_KIND,
  counterForDirection,
  countersFor,
  exceeds,
  holdRequired,
  looseAttestationRequired,
  lotRequired,
  NOTE_MAX,
  NOTE_MIN,
  noteLength,
  reasonForDirection,
  reasonsFor,
  stockKindsFor,
  wipFence,
  wipLimitApplies,
  type AdjustCounter,
  type AdjustDirection,
  type AdjustReason,
} from "../lib/adjust";
import { inventoryErrorMessage } from "../lib/errors";
import { formatMeasure } from "../lib/measure";
import { validateAdjustSearch, type AdjustSearch } from "./search-params";

/** The newest batches a product's picker lists (the transfer line's cap). */
const LOT_OPTIONS = 50;

export function StockAdjustScreen() {
  const { t } = useTranslation();
  const perms = useInventoryPermissions();
  const raw = useSearch({ strict: false });
  const prefill = useMemo(() => validateAdjustSearch(raw as Record<string, unknown>), [raw]);
  // "Record another" (and "Start over") start again from the SAME link, so a
  // storekeeper working down a list of D-308 leftovers keeps the order's plant
  // and product. The quantity is the one thing a second adjustment never
  // inherits: the #282 review found a second press crediting the same copper.
  const [round, setRound] = useState(0);
  const effective = useMemo<AdjustSearch>(
    () => (round === 0 ? prefill : { ...prefill, quantity: undefined }),
    [prefill, round],
  );

  if (!perms.isResolved) {
    return <CardSkeleton lines={8} label={t("inventory.adjust.loading")} />;
  }
  // Fail-closed mirror (hooks/permissions.ts): a custom role holding the code
  // reads this too, and the server is the boundary either way — a control
  // offered to a role the server refuses is the dead call the doctrine forbids.
  if (!perms.has("inventory.adjust")) {
    return (
      <section className="stock-adjust" data-testid="stock-adjust-forbidden">
        <ForbiddenState
          title={t("inventory.adjust.forbiddenTitle")}
          body={t("inventory.adjust.forbidden")}
        />
      </section>
    );
  }
  return (
    <AdjustForm
      key={`${JSON.stringify(prefill)}:${String(round)}`}
      prefill={effective}
      onAnother={() => {
        setRound((n) => n + 1);
      }}
    />
  );
}

/**
 * The loose row for this batch at this place. `truncated` means the place
 * holds more level rows than one page (`next_cursor`), so a loose row that is
 * not on the page is UNKNOWN rather than absent: the form then does not claim
 * "nothing lies loose here" and lets the server decide.
 */
function sumLoose(
  rows: readonly StockLevel[],
  lotId: string,
  truncated: boolean,
): { qty: string; uom: string } | "unknown" | undefined {
  const loose = rows.filter(
    (row) =>
      row.stock_unit_id === null && (lotId.length > 0 ? row.lot_id === lotId : row.lot_id === null),
  );
  const first = loose[0];
  if (first === undefined) return truncated ? "unknown" : undefined;
  // One (product, location, lot) carries one loose row (§2.5's identity); a
  // second would be a cache defect, and the first row is what the server's
  // cover check reads.
  return { qty: first.on_hand, uom: first.uom };
}

function AdjustForm({ prefill, onAnother }: { prefill: AdjustSearch; onAnother: () => void }) {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const intent = useIntentKey();
  const adjust = useAdjustStock();

  const [direction, setDirection] = useState<AdjustDirection>(prefill.direction ?? "increase");
  const [facilityId, setFacilityId] = useState(prefill.facility_id ?? "");
  const [productId, setProductId] = useState(prefill.product_id ?? "");
  const [lotId, setLotId] = useState(prefill.lot_id ?? "");
  const [locationId, setLocationId] = useState(prefill.location_id ?? "");
  const [counter, setCounter] = useState<AdjustCounter>(
    counterForDirection(prefill.direction ?? "increase", prefill.counter) ??
      countersFor(prefill.direction ?? "increase")[0] ??
      "found",
  );
  const [quantityText, setQuantityText] = useState(prefill.quantity ?? "");
  const [uom, setUom] = useState(prefill.uom ?? "");
  const [reason, setReason] = useState<AdjustReason | "">(
    reasonForDirection(prefill.direction ?? "increase", prefill.reason) ?? "",
  );
  const [note, setNote] = useState("");
  const noteTouched = useRef(false);
  const directionTouched = useRef(false);
  const [alwaysHold, setAlwaysHold] = useState(false);
  const [looseConfirmed, setLooseConfirmed] = useState(false);
  // P3-H-3 (D-342 ruling 2, D-391 ruling 3): a form opened from a work order
  // records the adjustment against it unless the person unticks it, and the
  // order then lists it under "Adjustments recorded against this order".
  const [recordAgainst, setRecordAgainst] = useState(true);
  const [outcome, setOutcome] = useState<AdjustOutcome | undefined>(undefined);
  // A network failure or a 5xx after a press: the adjustment may have posted.
  // The form locks until the same key replays or the person starts over.
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const [cleared, setCleared] = useState<string[]>([]);

  // ---- reference reads ------------------------------------------------------
  const facilities = useFacilities();
  const facilityItems = facilities.data?.items ?? [];
  const chosenLocation = useLocationPathIndex(locationId.length > 0 ? [locationId] : []);
  const location = chosenLocation.byId.get(locationId);
  const product = useProductCard(productId);
  const uoms = useUomIndex();
  const conversions = useProductConversions(productId.length > 0 ? [productId] : []);
  const workOrder = useWorkOrderRef(prefill.work_order_id ?? "");

  // The container the material came off (D-308 round 2): by id, or by the
  // serial a floor scan carried.
  const unitById = useStockUnit(prefill.unit_id ?? "");
  const serial = prefill.unit_id === undefined ? (prefill.unit_serial ?? "") : "";
  const unitsBySerial = useStockUnits({ serial_q: serial, limit: 5 }, serial.length > 0);
  const bySerial = unitsBySerial.data?.items.find(
    (unit) => unit.serial_code.toLowerCase() === serial.toLowerCase(),
  );
  const container = unitById.data?.unit ?? bySerial;
  const containerGone =
    (prefill.unit_id !== undefined &&
      unitById.error instanceof RequestError &&
      unitById.error.httpStatus === 404) ||
    (serial.length > 0 && unitsBySerial.data !== undefined && bySerial === undefined);
  // A container speaks for the material only while the form is about ITS
  // product: a reader who picks another product has left it behind.
  const containerApplies =
    container !== undefined &&
    (container.product_id === null || container.product_id === productId);
  const rule = containerApplies ? containerRule(container.status) : undefined;

  const seededLotId = prefill.lot_id ?? container?.lot_id ?? undefined;
  const prefillLot = useLot(seededLotId !== undefined && lotId === seededLotId ? lotId : "");
  const lots = useLots({ product_id: productId, limit: LOT_OPTIONS }, productId.length > 0);

  // The plant: chosen, else the prefilled location's, else the work order's
  // (a spec 04 S7 link names the order but no place), else the only one.
  const effectiveFacility =
    facilityId.length > 0
      ? facilityId
      : (location?.facility_id ??
        workOrder.data?.facilityId ??
        container?.facility_id ??
        (facilityItems.length === 1 ? (facilityItems[0]?.id ?? "") : ""));

  // A truck is written off to scrap and nothing else (§9.10); the select
  // offers only what this place may be booked against.
  const counterOptions = countersFor(direction, location?.kind);
  const activeCounter: AdjustCounter = counterOptions.includes(counter)
    ? counter
    : (counterOptions[0] ?? counter);

  const supplierLoc = usePlantLocationOfKind(effectiveFacility, COUNTER_KIND.found);
  const productionLoc = usePlantLocationOfKind(effectiveFacility, COUNTER_KIND.wip);
  const scrapLoc = usePlantLocationOfKind(effectiveFacility, COUNTER_KIND.scrap);
  const counterQuery =
    activeCounter === "found" ? supplierLoc : activeCounter === "wip" ? productionLoc : scrapLoc;
  const counterLocation = counterQuery.data;

  // ---- stock context --------------------------------------------------------
  const levelsHere = useLevels(
    {
      product_id: productId,
      location_id: locationId,
      ...(lotId.length > 0 ? { lot_id: lotId } : {}),
      limit: 200,
    },
    productId.length > 0 && locationId.length > 0,
  );
  const wipLevels = useLevels(
    {
      product_id: productId,
      location_id: productionLoc.data?.id ?? "",
      ...(lotId.length > 0 ? { lot_id: lotId } : {}),
      limit: 200,
    },
    productId.length > 0 && activeCounter === "wip" && productionLoc.data !== undefined,
  );

  // ---- a container the link named fills what the link did not ------------
  const containerProduct = container?.product_id ?? null;
  const containerLot = container?.lot_id ?? null;
  useEffect(() => {
    if (containerProduct === null) return;
    if (productId.length === 0 && prefill.product_id === undefined) {
      setProductId(containerProduct);
      if (lotId.length === 0 && containerLot !== null) setLotId(containerLot);
    }
  }, [containerLot, containerProduct, lotId, prefill.product_id, productId]);

  // ---- the note names the work order the link came from ------------------
  // A manual adjustment carries no source document, so the order's number in
  // the note is what traces it back (the #282 review). The person may edit
  // it; it is filled once, and only into an empty note nobody has typed in.
  const workOrderName =
    prefill.work_order_id === undefined
      ? undefined
      : `${term("work_order")} ${workOrder.data?.woNo ?? shortId(prefill.work_order_id)}`;
  const workOrderSettled = prefill.work_order_id !== undefined && !workOrder.isPending;
  useEffect(() => {
    if (!workOrderSettled || workOrderName === undefined) return;
    if (noteTouched.current || note.length > 0) return;
    noteTouched.current = true;
    setNote(
      prefill.reason === "data_correction"
        ? t("inventory.adjust.noteReceipt", { name: workOrderName })
        : workOrder.data?.status === "closed"
          ? t("inventory.adjust.noteLeftoverClosed", { name: workOrderName })
          : t("inventory.adjust.noteLeftover", { name: workOrderName }),
    );
  }, [note, prefill.reason, t, workOrder.data?.status, workOrderName, workOrderSettled]);

  // ---- prefill that no longer resolves is CLEARED, and said so ------------
  useEffect(() => {
    const gone: string[] = [];
    if (
      facilityId.length > 0 &&
      facilityId === prefill.facility_id &&
      facilities.data !== undefined &&
      (!facilities.data.items.some((f) => f.id === facilityId) ||
        (location !== undefined && location.facility_id !== facilityId))
    ) {
      // A plant the reader cannot see, or one that contradicts the place the
      // same link named: the place is more specific, so its plant wins.
      setFacilityId("");
      gone.push("facility");
    }
    if (
      locationId.length > 0 &&
      locationId === prefill.location_id &&
      !chosenLocation.isPending &&
      location === undefined
    ) {
      setLocationId("");
      gone.push("location");
    }
    if (
      productId.length > 0 &&
      productId === prefill.product_id &&
      product.error instanceof RequestError &&
      product.error.httpStatus === 404
    ) {
      setProductId("");
      setLotId("");
      gone.push("product");
    }
    if (
      lotId.length > 0 &&
      lotId === prefill.lot_id &&
      prefillLot.error instanceof RequestError &&
      prefillLot.error.httpStatus === 404
    ) {
      setLotId("");
      gone.push("lot");
    }
    if (containerGone && !cleared.includes("unit")) gone.push("unit");
    if (location !== undefined && !stockKindsFor(direction).includes(location.kind)) {
      if (
        location.kind === "in_transit" &&
        direction === "increase" &&
        prefill.direction === undefined &&
        locationId === prefill.location_id &&
        !directionTouched.current
      ) {
        // An entry on a truck that named no direction: a truck can only be
        // written off, so the form turns to a decrease and keeps the place.
        directionTouched.current = true;
        setDirection("decrease");
        setReason((prev) => (prev === "" ? "" : (reasonForDirection("decrease", prev) ?? "")));
      } else {
        // A place the form cannot name as the stock side (a link that named
        // the plant's work in progress, or a truck on an increase) is
        // cleared, never posted: the counter side is chosen by its select.
        setLocationId("");
        gone.push("location-kind");
      }
    }
    if (gone.length > 0) setCleared((prev) => [...prev, ...gone]);
  }, [
    direction,
    chosenLocation.isPending,
    cleared,
    containerGone,
    facilities.data,
    facilityId,
    location,
    locationId,
    lotId,
    prefill.direction,
    prefill.facility_id,
    prefill.location_id,
    prefill.lot_id,
    prefill.product_id,
    prefillLot.error,
    product.error,
    productId,
  ]);

  useEffect(() => {
    if (facilities.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [facilities.error, navigate]);

  // The product's base unit is the default, once the product is known.
  const baseUomCode = useMemo(() => {
    const baseId = product.data?.base_uom_id;
    return (uoms.data?.items ?? []).find((u) => u.id === baseId)?.code;
  }, [product.data?.base_uom_id, uoms.data]);
  useEffect(() => {
    if (uom.length === 0 && baseUomCode !== undefined) setUom(baseUomCode);
  }, [baseUomCode, uom]);

  const unitOptions = useMemo(() => {
    const codes = new Set<string>();
    if (baseUomCode !== undefined) codes.add(baseUomCode);
    const byId = new Map((uoms.data?.items ?? []).map((u) => [u.id, u.code]));
    for (const conversion of conversions.byId.get(productId) ?? []) {
      const from = byId.get(conversion.from_uom_id);
      const to = byId.get(conversion.to_uom_id);
      if (from !== undefined) codes.add(from);
      if (to !== undefined) codes.add(to);
    }
    if (uom.length > 0) codes.add(uom);
    return [...codes];
  }, [baseUomCode, conversions.byId, productId, uom, uoms.data]);

  // ---- states the whole screen takes ---------------------------------------
  if (facilities.fetchStatus === "paused" && facilities.data === undefined) {
    return (
      <OfflineState
        title={t("inventory.adjust.offlineTitle")}
        body={t("inventory.adjust.offlineBody")}
      />
    );
  }
  if (facilities.isPending || facilities.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={8} label={t("inventory.adjust.loading")} />;
  }
  if (facilities.error) {
    return (
      <ErrorState
        title={t("inventory.adjust.errorTitle")}
        detail={facilities.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void facilities.refetch()}
        requestId={requestIdOf(facilities.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }
  if (facilityItems.length === 0) {
    return (
      <EmptyState
        title={t("inventory.adjust.noPlantTitle")}
        body={t("inventory.adjust.noPlantBody")}
      />
    );
  }

  const tracking = product.data?.tracking;
  const seededLot = prefillLot.data?.lot;
  const lotItems = [
    ...(seededLot !== undefined && !(lots.data?.items ?? []).some((lot) => lot.id === seededLot.id)
      ? [seededLot]
      : []),
    ...(lots.data?.items ?? []),
  ];
  const lotQcStatus =
    lotId.length > 0 ? lotItems.find((lot) => lot.id === lotId)?.qc_status : undefined;
  const needsLot = lotRequired(tracking);
  const loose = sumLoose(levelsHere.data?.items ?? [], lotId, levelsHere.data?.next_cursor != null);
  const looseQty =
    loose === "unknown"
      ? undefined
      : (loose?.qty ?? (levelsHere.data !== undefined ? "0" : undefined));
  const pinned = (levelsHere.data?.items ?? []).filter(
    (row) => row.stock_unit_id !== null && (lotId.length === 0 || row.lot_id === lotId),
  );
  const firstPinned = pinned[0]?.stock_unit_id ?? undefined;
  const wip = sumLoose(wipLevels.data?.items ?? [], lotId, wipLevels.data?.next_cursor != null);
  const wipQty =
    wip === "unknown" ? undefined : (wip?.qty ?? (wipLevels.data !== undefined ? "0" : undefined));
  const wipBelowZero = wipQty !== undefined && !wipLimitApplies(wipQty);
  const inBase = baseUomCode !== undefined && uom === baseUomCode;
  const looseUom = (loose !== "unknown" ? loose?.uom : undefined) ?? baseUomCode ?? "";
  const wipUom = (wip !== "unknown" ? wip?.uom : undefined) ?? baseUomCode ?? "";

  const quantity = checkQuantity(quantityText, i18n.language);
  const quantityValue = quantity.ok ? quantity.value : undefined;
  const overLoose =
    direction === "decrease" &&
    inBase &&
    quantityValue !== undefined &&
    exceeds(quantityValue, looseQty);
  const overWip =
    direction === "increase" &&
    activeCounter === "wip" &&
    inBase &&
    quantityValue !== undefined &&
    wipLimitApplies(wipQty) &&
    exceeds(quantityValue, wipQty);
  const nothingLoose =
    direction === "decrease" && looseQty !== undefined && !exceeds(looseQty, "0");
  const fence = wipFence({
    direction,
    counter: activeCounter,
    placeKind: location?.kind,
    lotQcStatus,
  });
  const containerBlocks = rule === "live" || rule === "held";
  const needsLooseTick =
    looseAttestationRequired(direction, tracking) && !(containerApplies && rule === "no_stock");
  const forcedHold = holdRequired(location?.kind);
  const noteN = noteLength(note);

  const stockPending =
    counterQuery.isPending ||
    (productId.length > 0 && locationId.length > 0 && levelsHere.isPending) ||
    (activeCounter === "wip" &&
      productId.length > 0 &&
      wipLevels.isPending &&
      productionLoc.data !== undefined);
  const complete =
    effectiveFacility.length > 0 &&
    productId.length > 0 &&
    (!needsLot || lotId.length > 0) &&
    locationId.length > 0 &&
    counterLocation !== undefined &&
    quantityValue !== undefined &&
    uom.length > 0 &&
    reason !== "" &&
    noteN >= NOTE_MIN &&
    noteN <= NOTE_MAX;
  const blocked =
    overLoose ||
    overWip ||
    nothingLoose ||
    fence !== undefined ||
    containerBlocks ||
    (needsLooseTick && !looseConfirmed);
  const disabledReason = stockPending
    ? t("inventory.adjust.waitStock")
    : !complete
      ? t("inventory.adjust.incomplete")
      : undefined;

  function changeDirection(next: AdjustDirection) {
    directionTouched.current = true;
    setDirection(next);
    setCounter((prev) => counterForDirection(next, prev) ?? countersFor(next)[0] ?? prev);
    setReason((prev) => (prev === "" ? "" : (reasonForDirection(next, prev) ?? "")));
    // `in_transit` is a write-off place only (§9.10): an increase cannot land there.
    if (location !== undefined && !stockKindsFor(next).includes(location.kind)) setLocationId("");
    adjust.reset();
  }

  function submit() {
    // A press while the first is in flight would carry the same key and post
    // once, but only the LAST call's callbacks run: the first answer would be
    // dropped and the in-progress 409 shown instead. Nothing is sent twice.
    if (adjust.isPending) return;
    // `complete` already holds the counter location and the quantity; the
    // compiler follows that alias, so they are not re-checked here.
    if (!complete || blocked) return;
    const body = adjustmentBody({
      direction,
      productId,
      lotId,
      stockLocationId: locationId,
      counterLocationId: counterLocation.id,
      quantity: quantityValue,
      uom,
      reason,
      note,
      workOrderId: recordAgainst ? prefill.work_order_id : undefined,
    });
    const hold = alwaysHold || forcedHold;
    const key = intent.keyFor(JSON.stringify({ body, alwaysHold: hold }));
    adjust.mutate(
      { body, alwaysHold: hold, idempotencyKey: key },
      {
        onSuccess: (result) => {
          intent.settle();
          setUnknownOutcome(false);
          setOutcome(result);
        },
        onError: (error) => {
          intent.settle(error);
          setUnknownOutcome(outcomeUnknown(error));
        },
      },
    );
  }

  const backLotCode = lotItems.find((l) => l.id === prefill.lot_id)?.lot_code;
  const backPlace = backPlaceOf(prefill, {
    lookup: t("inventory.nav.lookup"),
    lot: backLotCode === undefined ? term("lot") : `${term("lot")} ${backLotCode}`,
    location: term("location"),
    approvals: t("inventory.nav.approvals"),
    workOrder: `${term("work_order")}${workOrder.data !== undefined ? ` ${workOrder.data.woNo}` : ""}`,
    conflict: t("production.conflicts.title"),
  });

  const head = (
    <PageHeader
      title={t("inventory.adjust.title")}
      actions={
        <>
          <BackLink
            prefill={prefill}
            label={
              backPlace === undefined ? undefined : t("inventory.adjust.back", { place: backPlace })
            }
          />
        </>
      }
    />
  );

  if (outcome !== undefined) {
    return (
      <section className="stock-adjust" data-testid="stock-adjust">
        {head}
        {outcome.kind === "posted" ? (
          <Banner kind="ok" data-testid="adjust-posted">
            {t("inventory.adjust.posted")}{" "}
            <Link
              to="/inventory/movements"
              search={{
                product_id: productId,
                location_id: locationId,
                movement_type: "adjustment",
              }}
              className="ds-lnk"
              data-testid="adjust-see-ledger"
            >
              {t("inventory.adjust.seeLedger")}
            </Link>
          </Banner>
        ) : (
          <Banner kind="warn" data-testid="adjust-held" data-request-id={outcome.requestId}>
            {t("inventory.adjust.held")}{" "}
            <Link
              to="/inventory/approvals/$id"
              params={{ id: outcome.requestId }}
              className="ds-lnk"
              data-testid="adjust-open-request"
            >
              {t("inventory.adjust.openRequest")}
            </Link>
          </Banner>
        )}
        <div className="form-actions">
          <Button data-testid="adjust-another" onClick={onAnother}>
            {t("inventory.adjust.another")}
          </Button>
        </div>
      </section>
    );
  }

  const containerName = container?.serial_code ?? "";

  return (
    <section className="stock-adjust" data-testid="stock-adjust">
      {head}
      <p className="inv-hint">{t("inventory.adjust.intro")}</p>
      {workOrderName !== undefined ? (
        <Banner kind="warn" data-testid="adjust-work-order">
          {workOrder.data?.status === "closed"
            ? t("inventory.adjust.workOrderClosed", { name: workOrderName })
            : t("inventory.adjust.workOrderOpen", { name: workOrderName })}
        </Banner>
      ) : null}
      {cleared.length > 0 ? (
        <p className="degraded-note is-warning" role="note" data-testid="adjust-prefill-cleared">
          {t("inventory.adjust.prefillMissing")}
        </p>
      ) : null}
      {unknownOutcome ? (
        <Banner kind="warn" data-testid="adjust-unknown">
          {t("inventory.adjust.unknown")}{" "}
          <Link
            to="/inventory/movements"
            search={{
              product_id: productId,
              location_id: locationId,
              movement_type: "adjustment",
            }}
            className="ds-lnk"
          >
            {t("inventory.adjust.unknownLedger")}
          </Link>{" "}
          <Link to="/inventory/approvals" className="ds-lnk">
            {t("inventory.adjust.unknownApprovals")}
          </Link>
        </Banner>
      ) : null}

      <form
        className="inv-panel adjust-form"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <fieldset className="adjust-fields" disabled={unknownOutcome}>
          <div className="ds-field">
            <span className="ds-field-label filter-chip-label">
              {t("inventory.adjust.directionLabel")}
            </span>
            <SegmentedControl<AdjustDirection>
              label={t("inventory.adjust.directionLabel")}
              data-testid="adjust-direction"
              value={direction}
              onChange={changeDirection}
              options={[
                { value: "increase", label: t("inventory.adjust.increase") },
                { value: "decrease", label: t("inventory.adjust.decrease") },
              ]}
            />
          </div>

          <Field label={term("facility")}>
            <Select
              value={effectiveFacility}
              data-testid="adjust-facility"
              onChange={(event) => {
                setFacilityId(event.target.value);
                setLocationId("");
              }}
            >
              {effectiveFacility.length === 0 ? (
                <option value="">{t("inventory.adjust.pickLot")}</option>
              ) : null}
              {facilityItems.map((facility) => (
                <option key={facility.id} value={facility.id}>
                  {facility.name}
                </option>
              ))}
            </Select>
          </Field>

          <div className="inv-label" data-testid="adjust-product">
            <span className="filter-chip-label">{t("inventory.adjust.product")}</span>
            <ProductPicker
              value={productId}
              onChange={(next) => {
                setProductId(next);
                setLotId("");
                setUom("");
              }}
              placeholder={t("inventory.adjust.productPlaceholder")}
              resolveLabel={(id) =>
                id === product.data?.id ? `${product.data.sku} · ${product.data.name}` : undefined
              }
            />
          </div>

          {productId.length > 0 ? (
            <Field label={term("lot")}>
              <Select
                value={lotId}
                data-testid="adjust-lot"
                onChange={(event) => {
                  setLotId(event.target.value);
                }}
              >
                <option value="">
                  {needsLot ? t("inventory.adjust.pickLot") : t("inventory.adjust.noLot")}
                </option>
                {lotItems.map((lot) => (
                  <option key={lot.id} value={lot.id}>
                    {lot.lot_code}
                  </option>
                ))}
              </Select>
              {needsLot && lotId.length === 0 ? (
                <span className="inv-hint" data-testid="adjust-lot-required">
                  {lots.data !== undefined && lotItems.length === 0
                    ? t("inventory.adjust.lotNone", { lot: term("lot") })
                    : t("inventory.adjust.lotRequired", { lot: term("lot") })}
                </span>
              ) : null}
              {lots.data?.next_cursor != null ? (
                <span className="inv-hint">
                  {t("inventory.transfers.lineLotTruncated", { n: LOT_OPTIONS })}
                </span>
              ) : null}
            </Field>
          ) : null}

          {containerApplies && rule !== undefined ? (
            <p
              className={rule === "no_stock" ? "inv-hint" : "form-error"}
              role={rule === "no_stock" ? "note" : "alert"}
              data-testid="adjust-container"
              data-rule={rule}
            >
              {t(`inventory.adjust.container.${rule}`, {
                serial: containerName,
                lot: term("lot"),
              })}{" "}
              <Link
                to="/inventory/units/$id"
                params={{ id: container.id }}
                className="ds-lnk"
                data-testid="adjust-container-open"
              >
                {t("inventory.adjust.containerOpen", { serial: containerName })}
              </Link>
            </p>
          ) : null}

          <LocationPicker
            testId="adjust-location"
            label={t("inventory.adjust.location")}
            value={locationId}
            onChange={setLocationId}
            facilityId={effectiveFacility.length > 0 ? effectiveFacility : undefined}
            isActive
            kinds={stockKindsFor(direction)}
          />
          {productId.length > 0 && locationId.length > 0 && levelsHere.data !== undefined ? (
            <p className="inv-hint" data-testid="adjust-loose">
              {looseQty === undefined
                ? t("inventory.adjust.looseUnknown")
                : exceeds(looseQty, "0")
                  ? t("inventory.adjust.loose", {
                      qty: formatMeasure(looseQty, { locale: i18n.language }),
                      uom: looseUom,
                    })
                  : t("inventory.adjust.looseNone")}
            </p>
          ) : null}
          {firstPinned !== undefined ? (
            <p className="inv-hint" data-testid="adjust-pinned">
              {t("inventory.adjust.pinned", { n: pinned.length })}{" "}
              <Link to="/inventory/units/$id" params={{ id: firstPinned }} className="ds-lnk">
                {t("inventory.adjust.pinnedOpen")}
              </Link>
            </p>
          ) : null}

          <Field label={t("inventory.adjust.counterLabel")}>
            <Select
              value={activeCounter}
              data-testid="adjust-counter"
              onChange={(event) => {
                setCounter(event.target.value as AdjustCounter);
              }}
            >
              {counterOptions.map((option) => (
                <option key={option} value={option}>
                  {option === "wip"
                    ? t("inventory.adjust.counter.wip", { path: productionLoc.data?.path ?? "…" })
                    : t(`inventory.adjust.counter.${option}`)}
                </option>
              ))}
            </Select>
            {counterQuery.error ? (
              <span className="form-error" role="alert" data-testid="adjust-counter-error">
                {inventoryErrorMessage(t, counterQuery.error, "inventory.adjust.errorTitle")}{" "}
                <button
                  type="button"
                  className="ds-lnk"
                  onClick={() => void counterQuery.refetch()}
                >
                  {t("inventory.common.retry")}
                </button>
              </span>
            ) : effectiveFacility.length > 0 &&
              counterQuery.isSuccess &&
              counterLocation === undefined ? (
              <span className="form-error" role="alert" data-testid="adjust-counter-missing">
                {t("inventory.adjust.counterMissing")}
              </span>
            ) : null}
          </Field>
          {activeCounter === "wip" && productId.length > 0 && wipLevels.data !== undefined ? (
            <p className="inv-hint" data-testid="adjust-wip">
              {t(wipBelowZero ? "inventory.adjust.wipOutput" : "inventory.adjust.wipHeld", {
                qty: formatMeasure(wipQty ?? "0", { locale: i18n.language }),
                uom: wipUom,
              })}
            </p>
          ) : null}
          {fence !== undefined ? (
            <p className="form-error" role="alert" data-testid="adjust-wip-fence">
              {t(`inventory.adjust.fence.${fence}`, { lot: term("lot") })}
            </p>
          ) : null}

          <div className="adjust-quantity-row">
            <Field label={t("inventory.adjust.quantity")}>
              <input
                className="ds-input"
                value={quantityText}
                inputMode="decimal"
                data-testid="adjust-quantity"
                aria-invalid={quantityText.trim().length > 0 && !quantity.ok}
                onChange={(event) => {
                  setQuantityText(event.target.value);
                }}
              />
            </Field>
            <Field label={t("inventory.adjust.unit")}>
              <Select
                value={uom}
                data-testid="adjust-uom"
                onChange={(event) => {
                  setUom(event.target.value);
                }}
              >
                {uom.length === 0 ? <option value="">…</option> : null}
                {unitOptions.map((code) => (
                  <option key={code} value={code}>
                    {code === baseUomCode ? t("inventory.adjust.unitBase", { code }) : code}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {prefill.expected_qty !== undefined ? (
            <p className="inv-hint" data-testid="adjust-expected">
              {t(
                prefill.from === "conflict"
                  ? "inventory.adjust.expectedFloor"
                  : "inventory.adjust.expectedOrder",
                {
                  qty: formatMeasure(prefill.expected_qty, { locale: i18n.language }),
                  uom: prefill.uom ?? uom,
                },
              )}
            </p>
          ) : null}
          {quantityText.trim().length > 0 && !quantity.ok ? (
            <p className="form-error" role="alert" data-testid="adjust-quantity-invalid">
              {t("inventory.adjust.quantityInvalid")}
            </p>
          ) : null}
          {nothingLoose ? (
            <p className="form-error" role="alert" data-testid="adjust-nothing-loose">
              {t("inventory.adjust.looseNone")}
            </p>
          ) : overLoose ? (
            <p className="form-error" role="alert" data-testid="adjust-over-loose">
              {t("inventory.adjust.overLoose", {
                qty: formatMeasure(looseQty, { locale: i18n.language }),
                uom: looseUom,
              })}
            </p>
          ) : null}
          {overWip ? (
            <p className="form-error" role="alert" data-testid="adjust-over-wip">
              {t("inventory.adjust.overWip", {
                qty: formatMeasure(wipQty, { locale: i18n.language }),
                uom: wipUom,
              })}
            </p>
          ) : null}
          {baseUomCode !== undefined && uom.length > 0 && !inBase ? (
            <p className="inv-hint" data-testid="adjust-other-unit">
              {t("inventory.adjust.otherUnit")}
            </p>
          ) : null}

          <Field label={t("inventory.adjust.reason")}>
            <Select
              value={reason}
              data-testid="adjust-reason"
              onChange={(event) => {
                setReason(reasonForDirection(direction, event.target.value) ?? "");
              }}
            >
              <option value="">{t("inventory.adjust.reasonPick")}</option>
              {reasonsFor(direction).map((code) => (
                <option key={code} value={code}>
                  {t(`inventory.vocab.reason.${code}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t("inventory.adjust.note")}>
            <textarea
              className="ds-input"
              value={note}
              rows={3}
              maxLength={NOTE_MAX}
              data-testid="adjust-note"
              onChange={(event) => {
                noteTouched.current = true;
                setNote(event.target.value);
              }}
            />
            <span className="inv-hint" data-testid="adjust-note-count">
              {noteN < NOTE_MIN
                ? t("inventory.adjust.noteShort", { n: noteN })
                : t("inventory.adjust.noteCount", { n: noteN })}
            </span>
          </Field>

          {needsLooseTick ? (
            <>
              <CheckboxField
                label={t("inventory.adjust.looseConfirm")}
                checked={looseConfirmed}
                onChange={setLooseConfirmed}
                data-testid="adjust-loose-confirm"
              />
              <p className="inv-hint" data-testid="adjust-loose-reason">
                {t("inventory.adjust.looseReason")}
              </p>
            </>
          ) : null}

          {workOrderName !== undefined ? (
            <>
              <CheckboxField
                label={t("inventory.adjust.recordAgainst", { name: workOrderName })}
                checked={recordAgainst}
                onChange={setRecordAgainst}
                data-testid="adjust-record-against"
              />
              <p className="inv-hint">{t("inventory.adjust.recordAgainstHint")}</p>
            </>
          ) : null}

          <CheckboxField
            label={t("inventory.adjust.askApproval")}
            checked={alwaysHold || forcedHold}
            disabled={forcedHold}
            onChange={setAlwaysHold}
            data-testid="adjust-always-hold"
          />
          <p className="inv-hint" data-testid={forcedHold ? "adjust-transit-hold" : undefined}>
            {forcedHold ? t("inventory.adjust.transitHold") : t("inventory.adjust.approvalHint")}
          </p>
        </fieldset>

        {adjust.isError ? (
          <p className="form-error" role="alert" data-testid="adjust-error">
            {inventoryErrorMessage(t, adjust.error, "inventory.adjust.failed")}
            {requestIdOf(adjust.error) !== undefined ? (
              <>
                {" "}
                <code>{requestIdOf(adjust.error)}</code>
              </>
            ) : null}
          </p>
        ) : null}

        <div className="form-actions">
          <Button
            type="submit"
            variant="primary"
            data-testid="adjust-submit"
            busy={adjust.isPending}
            busyLabel={t("inventory.adjust.saving")}
            disabled={disabledReason !== undefined || blocked}
          >
            {t("inventory.adjust.submit")}
          </Button>
          {unknownOutcome ? (
            <Button data-testid="adjust-start-over" onClick={onAnother}>
              {t("inventory.adjust.startOver")}
            </Button>
          ) : null}
          {disabledReason !== undefined ? (
            <span className="inv-hint" data-testid="adjust-disabled-reason">
              {disabledReason}
            </span>
          ) : null}
        </div>
      </form>
    </section>
  );
}

interface BackPlaces {
  lookup: string;
  lot: string;
  location: string;
  approvals: string;
  workOrder: string;
  conflict: string;
}

function backPlaceOf(prefill: AdjustSearch, places: BackPlaces): string | undefined {
  switch (prefill.from) {
    case "lookup":
      return places.lookup;
    case "lot":
      return prefill.lot_id === undefined ? undefined : places.lot;
    case "location":
      return places.location;
    case "approvals":
      return places.approvals;
    case "work_order":
      return prefill.work_order_id === undefined ? undefined : places.workOrder;
    case "conflict":
      return places.conflict;
    case undefined:
      return undefined;
  }
}

/** The way back to the screen the person came from, when the link said which. */
function BackLink({ prefill, label }: { prefill: AdjustSearch; label: string | undefined }) {
  if (label === undefined) return null;
  const common = { className: "ds-lnk", "data-testid": "adjust-back" } as const;
  switch (prefill.from) {
    case "lookup":
      return (
        <Link to="/inventory/lookup" {...common}>
          {label}
        </Link>
      );
    case "lot":
      return prefill.lot_id === undefined ? null : (
        <Link to="/inventory/lots/$id" params={{ id: prefill.lot_id }} {...common}>
          {label}
        </Link>
      );
    case "location":
      return (
        <Link to="/inventory/locations" {...common}>
          {label}
        </Link>
      );
    case "approvals":
      return (
        <Link to="/inventory/approvals" {...common}>
          {label}
        </Link>
      );
    case "work_order":
      return prefill.work_order_id === undefined ? null : (
        <Link to="/production/work-orders/$id" params={{ id: prefill.work_order_id }} {...common}>
          {label}
        </Link>
      );
    case "conflict":
      return (
        <Link to="/production/conflicts" {...common}>
          {label}
        </Link>
      );
    case undefined:
      return null;
  }
}
